import { html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import componentStyles from './icon-nav.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';

import '../icon/icon';
import '../link-button/link-button';
import '../dropdown-list/dropdown-list';

export default class IconNav extends OutlineElement {
  static styles = [super.styles, componentStyles];

  static get properties() {
    return {
      /**
       * { onClick?: Function, }
       */
      links: { type: Array },
      listDirection: { type: String },
      navClass: { type: String },
      variant: {type: String},
      title: { type: String },
      hideText: { type: Boolean },
      hasSeparator: { type: Boolean },
      hasPadding: { type: Boolean },
      isHPSite: { type: Boolean },
      firstName: { type: String },
      loggedIn: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.listDirection = '';
    this.navClass = '';
    this.variant = '';
    this.hideText = false;
    this.firstName = '';
    this.loggedIn = false;
    this.hasPadding = true;
    this.hasSeparator = false;
    this.isHPSite = false;
    this.dropdownListState = {};
  }

  getDropdownParent(event) {
    const buttonTag = 'HF-LINK-BUTTON';
    if (event.target.tagName === buttonTag) {
      return event.target;
    }

    if (event.target.parentNode.tagName === buttonTag) {
      return event.target.parentNode;
    }

    if (event.target.parentNode.parentNode.tagName === buttonTag) {
      return event.target.parentNode.parentNode;
    }
  }

  closeAllDropdowns() {
    Object.keys(this.dropdownListState).forEach(state => {
      this.dropdownListState[state] = false;
    });
  }

  setActiveDropdown(dropdownKey, state) {
    this.dropdownListState = {
      ...this.dropdownListState,
      [dropdownKey]: state,
    };
  }

  onClick(event, linkOnClick) {
    const dropdownToggleClass = 'icon-navigation--dropdown';
    const composedPathElement = event.composedPath()[0];

    if (
      composedPathElement.classList.contains('dropdown-list__link') ||
      composedPathElement.classList.contains('dropdown-list__item')
    ) {
      return;
    }

    if (
      event.target.classList.contains(dropdownToggleClass) ||
      event.target.parentNode.classList.contains(dropdownToggleClass) ||
      event.target.parentNode.parentNode.classList.contains(dropdownToggleClass)
    ) {
      event.preventDefault();
      const linkButton = this.getDropdownParent(event);
      const dropdownKey = linkButton.getAttribute('dropdownkey');
      const currentState = this.dropdownListState[dropdownKey];

      this.closeAllDropdowns();
      this.setActiveDropdown(dropdownKey, !currentState);
      this.requestUpdate();
    }

    if (linkOnClick) {
      linkOnClick();
    }
  }

  getDropdownKey(text) {
    return text
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '_');
  }

  getDropdownState(link) {
    const key = this.getDropdownKey(link.text);

    if (!this.dropdownListState.hasOwnProperty(key)) {
      this.dropdownListState[key] = false;
    }

    return this.dropdownListState[key];
  }

  renderDropdownList(link) {
    const key = this.getDropdownKey(link.text);
    return html`<hf-dropdown-list
      key="${key}"
      .open="${this.dropdownListState[key]}"
      .list="${link.dropdownlist}"
      .isHPSite="${this.isHPSite}"
    ></hf-dropdown-list>`;
  }

  renderLinkText(link) {
    if (
      !this.isHPSite &&
      this.loggedIn &&
      this.navClass === 'secondary' &&
      link.text.toLowerCase() === 'log in'
    ) {
      return 'Welcome ' + this.firstName;
    }

    return link.text;
  }

  isDropdown(link) {
    if (
      !this.isHPSite &&
      this.navClass === 'secondary' &&
      link.text.toLowerCase() === 'log in'
    ) {
      return this.loggedIn;
    }

    return link.dropdownlist;
  }

  dirtyUrlReplaceForLogin(link) {
    if (
      !this.isHPSite &&
      this.navClass === 'secondary' &&
      link.text.toLowerCase() === 'log in' &&
      !this.loggedIn
    ) {
      return '/saml/login';
    }

    return link.url;
  }

  renderDropdown(link) {
    const dropdownListHTML = html`
      <hf-icon
        class="${classMap({
          'icon-navigation__dropdown': true,
          'icon-navigation__dropdown--rotate': this.getDropdownState(link),
        })}"
        name="chevron-down"
        height="9"
        width="14"
      ></hf-icon>
      ${this.renderDropdownList(link)}
    `;

    return this.isDropdown(link) ? dropdownListHTML : nothing;
  }

  render() {
    return html`
      <nav role="navigation" title="${this.title}">
        <ul
          class="${classMap({
            'icon-navigation': true,
            [`icon-navigation--${this.listDirection}`]: !!this.listDirection,
            [`icon-navigation--${this.navClass}`]: !!this.navClass,
            'icon-navigation--hp': this.isHPSite,
          })}"
        >
          ${this.links.map(link => {
            console.log(this.isDropdown(link) ? link : '');
            return html`<li
              translate="${this.isDropdown(link) ? 'no' : 'yes'}"
              class="${classMap({
                'icon-navigation--item': true,
                'icon-navigation--dropdown': this.isDropdown(link),
                'icon-navigation--separator': this.hasSeparator,
                'notranslate': this.isDropdown(link),
              })}"
            >
              ${link.label
                ? html`<span class="icon-navigation__label"
                    >${link.label}</span
                  >`
                : ''}
              <hf-link-button
                ariaLabel="${ifDefined(link.text)}"
                href=${this.dirtyUrlReplaceForLogin(link)}
                target="${link.target ? link.target : '_self'}"
                icon="${ifDefined(link.iconname)}"
                iconcolor="${ifDefined(link.iconcolor)}"
                iconDirection="left"
                linkClass="btn__link--icon-navigation-${this.navClass}"
                @click=${event => this.onClick(event, link.onClick)}
                ignoreExternalUrl
                .isHPSite="${this.isHPSite}"
                dropdownkey=${ifDefined(
                  this.isDropdown(link)
                    ? this.getDropdownKey(link.text)
                    : undefined
                )}
              >
                <span
                  class="${classMap({
                    'icon-navigation--hidden': this.hideText,
                    'icon-navigation--dropdown': this.isDropdown(link),
                  })}"
                  ${this.isDropdown(link) ? 'translate="no"' : ''}
                >
                  ${this.renderLinkText(link)} ${this.renderDropdown(link)}
                </span>
              </hf-link-button>
            </li>`;
          })}
          ${this.navClass === 'secondary'
            ? html` <li
                class="${classMap({
                  'icon-navigation--item': true,
                  'icon-navigation__language': true,
                  // 'icon-navigation--separator': this.hasSeparator,
                })}"
              >
                <hf-icon
                  class="${classMap({
                    'icon-navigation__dropdown': true,
                  })}"
                  name="chevron-down"
                  height="9"
                  width="14"
                ></hf-icon>
              </li>`
            : nothing}
        </ul>
      </nav>
    `;
  }
}
customElements.define('hf-icon-nav', IconNav);
